import React, { ReactNode, useEffect, useMemo } from 'react'

import { MapPinIcon } from '@heroicons/react/24/outline'
import cn from 'classnames'
import { ErrorMessage, useField } from 'formik'
import _debounce from 'lodash/debounce'

import Spinner from '@/components/Spinner'
import { useNewGetAddressDetailsMutation, useNewGetAddressListMutation } from '@/services/property'
import { IAddress } from '@/types/services'
import { AddresSearchHint } from '@/types/unlock'

const NoOp: VoidFunction = () => {
  return
}

interface IHint {
  address: AddresSearchHint
  onClick: () => void
}

function Hint({ address, onClick }: IHint) {
  if (typeof address === 'string')
    return (
      <li
        onClick={onClick}
        className='odd:bg-unlock-icegray p-3 even:bg-white cursor-pointer'
      >
        <div
          className='text-sm text-unlock-gray font-semibold'
          data-test='manuallycreatemaxdeal-propertyaddress-list-addressoption'
        >
          {address}
        </div>
      </li>
    )
  else
    return (
      <li
        onClick={onClick}
        className='odd:bg-unlock-icegray p-3 even:bg-white cursor-pointer'
      >
        <div className='text-sm text-unlock-gray font-semibold'>{address.addressLine1}</div>
        <span className='capitalize font-semibold text-unlock-gray'>
          {address.city.toLowerCase()}
        </span>
        {', '}
        <span className='font-semibold text-unlock-gray'>{address.state}</span>
        {' - '}
        <span className='text-unlock-purple'>{address.zip}</span>
      </li>
    )
}

interface IAddressInput {
  name: string
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  initialValue?: IAddress
  addressHintsClassnames?: string
  isRequired?: boolean
  notFoundCallback?: VoidFunction
  showIcon?: boolean
  button?: ReactNode
  autoFocus?: boolean
  style?: 'default' | 'classic' | 'none'
  handleSearchSuccessful?: (newAddress: IAddress) => void
  customClasses?: string
}

export function AddressInput({
  name,
  placeholder = '',
  disabled = false,
  readOnly = false,
  addressHintsClassnames = '',
  notFoundCallback = NoOp,
  showIcon = false,
  autoFocus = false,
  style = 'default',
  button,
  handleSearchSuccessful,
  customClasses = null,
}: IAddressInput) {
  const [field, , helpers] = useField({ name })

  const [isShowingAddressHints, setIsShowingAddressHints] = React.useState(false)

  const inputRef = React.useRef(null)
  const dropdownRef = React.useRef(null)

  const [getAddressList, { data: addressListData, isLoading: isAddressListLoading }] =
    useNewGetAddressListMutation()

  const [getAddressDetails, { isLoading: isAddressDetailsLoading }] =
    useNewGetAddressDetailsMutation()

  const debounceFn = useMemo(() => _debounce((e) => handleGetAddressList(e), 500), [])

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsShowingAddressHints(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const onInputChange = async (event) => {
    const { value } = event.target
    helpers.setValue(value)
    debounceFn(value)
  }

  const handleGetAddressList = async (value) => {
    const startWith3Digits = /^\d{3}/.test(value)
    if (!startWith3Digits && value.length <= 6) {
      setIsShowingAddressHints(false)
      return
    }
    setIsShowingAddressHints(true)
    const params = { new_address_search_engine: true }
    await getAddressList({ query: value, ...params }).unwrap()
  }

  const onAddressSelected = async (address: AddresSearchHint) => {
    let selectedAddress = null
    try {
      const params = { new_address_search_engine: true }
      selectedAddress = await getAddressDetails({
        address,
        ...params,
      }).unwrap()
    } catch (error) {
      // Some edge cases were the address is found on the find-address
      // but not on the address-details, so if this happens redirect
      // user to the not found callback
      if (error?.status === 400) {
        notFoundCallback()
      }
    }

    if (!selectedAddress) return

    const newAddress: IAddress = {
      street: selectedAddress.address.streetAddress,
      unit_number: selectedAddress.address.unitNumber,
      city: selectedAddress.address.city,
      county: selectedAddress.address.county,
      state: selectedAddress.address.state,
      zip_code: selectedAddress.address.zipcode,
      corelogic_property_id: selectedAddress.address.corelogicPropertyId,
      fips_code: selectedAddress.address.fipsCode,
      parcel_number: selectedAddress.address.parcelNumber,
      latitude: selectedAddress.address.latitude,
      longitude: selectedAddress.address.longitude,
    }
    helpers.setValue(newAddress.street)
    helpers.setTouched(true)
    setIsShowingAddressHints(false)
    if (handleSearchSuccessful) handleSearchSuccessful(newAddress)
  }

  const inputWrapperClasses = cn('field-text', {
    'read-only-field pr-0': readOnly,
    'disabled-field': disabled,
  })

  const isLoading = isAddressListLoading || isAddressDetailsLoading

  const fieldClasses =
    style === 'none'
      ? ''
      : style === 'default'
        ? 'rounded w-full border border-gray-200 focus:border-gray-200 focus:ring-gray-400 text-[14px] !text-black placeholder:text-gray-400 !font-normal h-[32px] disabled:bg-gray-400 disabled:border-gray-200 disabled:text-gray-400'
        : 'classic-input'

  return (
    <div ref={dropdownRef}>
      <div className={`w-full`}>
        <div className={`${inputWrapperClasses}`}>
          {showIcon && (
            <MapPinIcon
              className='text-unlock-gray lg:mx-4'
              width={26}
            />
          )}
          <input
            value={field.value}
            ref={inputRef}
            name={name}
            type='text'
            className={`${fieldClasses} ${customClasses}`}
            data-test='manuallycreatemaxdeal-propertyaddress-input'
            placeholder={placeholder}
            onChange={onInputChange}
            onBlur={() => !isShowingAddressHints && helpers.setTouched(true)}
            onClick={() => {
              if (field.value) setIsShowingAddressHints(true)
            }}
            disabled={disabled}
            readOnly={readOnly}
            autoComplete='off'
            autoFocus={autoFocus}
          />
        </div>
        {button}
      </div>

      {isShowingAddressHints && (
        <>
          <div
            className={`w-[130%] bg-white border border-unlock-icegray shadow-xl mx-auto absolute z-10 ${addressHintsClassnames}`}
          >
            {isLoading ? (
              <div className='w-full min-h-[50px] justify-center items-center'>
                <Spinner className='mt-4' />
              </div>
            ) : addressListData ? (
              <ul
                className='h-[250px] overflow-y-auto'
                data-test='manuallycreatemaxdeal-propertyaddress-list'
              >
                {addressListData?.map((address, i) => (
                  <>
                    <Hint
                      key={i}
                      address={address}
                      onClick={() => onAddressSelected(address)}
                    />
                    {i < addressListData.length - 1 && (
                      <hr
                        className='h-[1px] border-none w-full border-gray-200'
                        style={{ borderTop: '2px dotted' }}
                      />
                    )}
                  </>
                ))}
              </ul>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

interface IRichAddressInput extends IAddressInput {
  label: string
  className?: string
}

export function RichAddressInput({
  label,
  name,
  className = '',
  button,
  ...props
}: IRichAddressInput) {
  return (
    <div
      key={name}
      className={`${className}`}
    >
      <div className='relative'>
        <div className='mb-2'>
          <label
            className='mb-2 text-sm !text-black text-weight-600'
            htmlFor={name}
          >
            {label}
          </label>
        </div>
        <AddressInput
          name={name}
          button={button}
          {...props}
        />
      </div>
      <ErrorMessage name={name}>
        {(msg) => <p className='label-text-alt text-red-500'>{msg}</p>}
      </ErrorMessage>
    </div>
  )
}
